/** @jsxImportSource @emotion/react */

import { useState, useEffect, FC } from "react";
import { css } from "@emotion/core";
import useNotification from "ui-modules/commons/hooks/use-notification";
import GridCell from "ui-modules/components/Layout/Grid/GridCell";
import Grid from "ui-modules/components/Layout/Grid";
import Skeleton from "ui-modules/components/Misc/Skeleton";
import rest from "../../utils/rest";
import Paper from "../../components/Paper";
import { calculateTableData, generatedInvoiceItemList } from "../../utils/commons";
import PaymentPanel from "../../components/PaymentPanel";
import Text from "ui-modules/components/DataDisplay/Text";
import Img from "ui-modules/components/Image/Img";
import zitelLogo from "../../../src/assets/images/zitel-logo.svg";

type ViewInvoiceItemType = {
  id: string;
  name: string;
  amount: number;
};

type StateTypes = {
  factors: ViewInvoiceItemType[];
  totalAmount: number;
  totalAmountFactor: number;
  isLoading: boolean;
  factorId: string;
  proformaId: string;
  customerId: string;
  isError: boolean;
  viewOnly: boolean;
};

type NoAuthProformaProps = {
  uuId?: string | null;
};

const NoAuthProforma: FC<NoAuthProformaProps> = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const uuId = queryParameters.get("uuid-code");
  const notification = useNotification();
  const [state, setState] = useState<StateTypes>({
    proformaId: "",
    customerId: "",
    factors: [],
    totalAmount: 0,
    totalAmountFactor: 0,
    isLoading: false,
    factorId: "",
    isError: false,
    viewOnly: false,
  });

  useEffect(() => {
    if (uuId) {
      rest
        .get(`${process.env.REACT_APP_BSS_SERVICE}/api/portal/sms-proforma?uuid-code=${uuId}`, {
          headers: {
            "X-Auth": "no-auth",
          },
        })
        .then((result) => {
          let invoiceItemListResp = result?.data?.invoiceItemList || [];
          if (invoiceItemListResp.length) {
            const invoiceItemListData = generatedInvoiceItemList(
              invoiceItemListResp,
              0,
              result?.data?.proformaDepositItemList || [],
              result?.data?.depositAmount || 0,
              0,
              false
            );
            setState((state: any) => ({
              ...state,
              proformaId: result?.data?.id || "",
              customerId: result?.data?.customer.id || "",
              factors: invoiceItemListData.invoiceItemList,
              totalAmount: invoiceItemListData.totalAmount,
              totalAmountFactor: invoiceItemListData.totalAmountFactor,
              isLoading: false,
              factorId: result?.data?.id || "",
              viewOnly: result?.data?.viewOnly ?? false,
            }));
          }
        })
        .catch(() => {
          setState((state: any) => ({
            ...state,
            isError: true,
          }));
          notification.error({ message: "خطا، لطفا مجددا تلاش نمایید." });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuId]);

  const renderFactors = () => {
    if (state.isLoading) {
      return (
        <table className="table" aria-label="simple table">
          <tbody>
            {["1", "2", "3"].map((factor) => (
              <tr key={factor}>
                <th align="right" scope="row" colSpan={2}>
                  <Skeleton />
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      if (state.totalAmountFactor < 0) {
        return (
          <div style={{ textAlign: "center", paddingTop: 50 }}>
            <Text type={"paragraph"}>برای پرداخت پیش فاکتور، لطفا با واحد فروش تماس بگیرید.</Text>
          </div>
        );
      } else {
        return (
          <table className="table" aria-label="simple table">
            <tbody>
              {state.factors.map((factor: { id: string; name: string }) => (
                <tr key={factor.id}>
                  <th align="right" scope="row">
                    <Text>{factor.name}</Text>
                  </th>
                  <td align="left">
                    <Text>{calculateTableData(factor, { type: "currency", pathData: "amount" })} ریال</Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
    }
  };

  const style = css`
    .inovice-wrapper {
      height: 95vh;
      text-align: center;
    }
    Img {
      width: 100px;
      margin-top: 20px;
    }
    table {
      margin-top: 20px;
      width: 100%;
      display: table;
      border-spacing: 0;
      border-collapse: collapse;
      tbody {
        display: table-row-group;
      }
      tr {
        color: inherit;
        display: table-row;
        outline: 0;
        vertical-align: middle;
      }
      th {
        padding-right: 20px;
        text-align: right;
        flex-direction: row-reverse;
        border-bottom: 1px solid rgba(81, 81, 81, 1);
      }
      td {
        display: table-cell;
        padding: 16px;
        font-size: 0.875rem;
        text-align: left;
        font-weight: 400;
        line-height: 1.43;
        border-bottom: 1px solid rgba(81, 81, 81, 1);
        vertical-align: inherit;
      }
      tr:last-child th,
      tr:last-child td {
        //padding-top: 100px;
        border-bottom: 0;
      }
      .handle-error {
        padding-top: 50px;
        display: inline-block;
      }
    }
  `;

  return (
    <Grid css={style}>
      <GridCell phone={4} tablet={8} desktop={12}>
        <Paper className="paper--content inovice-wrapper">
          <Img src={zitelLogo} alt="zitel-logo" />
          {uuId && !state.isError ? (
            <>
              {renderFactors()}
              <PaymentPanel
                amount={state.totalAmountFactor < 0 ? 0 : state.totalAmount}
                disabled={state.totalAmount <= 0 || state.totalAmountFactor <= 0 || state.isLoading}
                proformasId={state.proformaId}
                loading={state.isLoading}
                customerId={state.customerId}
                hidePaymentButton={state.viewOnly}
              />
            </>
          ) : (
            <div className="handle-error">
              <Text>خطایی در نمایش فاکتور رخ داده است</Text>
            </div>
          )}
        </Paper>
      </GridCell>
    </Grid>
  );
};

export default NoAuthProforma;
